import React, { useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import useStyles from './styles';
import BillingDFORow from './components/BillingDFORow';
import TablePagination from '../../components/CustomTablePagination';
import { getDfoCustomerNumber } from '../../redux/selectors/auth';
import { getBuilderInvoices } from '../../services/billing';
import { ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../constants/table';
import {
  BillingApiResponse,
  BillingInvoice,
} from './types';

type FilterOption = 'all' | 'unpaid' | 'paid';

const BillingDFO: React.FC = () => {
  const [filterOption, setFilterOption] = useState<FilterOption>('all');
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const classes = useStyles();
  const dfoCustomerNumber = useSelector(getDfoCustomerNumber);

  useEffect(() => {
    if (!dfoCustomerNumber) {
      setInvoices([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let isMounted = true;

    const loadInvoices = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await getBuilderInvoices<BillingApiResponse>(dfoCustomerNumber);

        if (!isMounted) {
          return;
        }

        setInvoices((data.items ?? []).map((invoice) => {
          const balance = invoice.amountDue ?? 0;
          const dueDate = invoice.dueDate ?? '';
          let status: string;
          if (invoice.status.toLowerCase() === 'paid' || balance <= 0) {
            status = 'Paid';
          } else if (new Date(dueDate) < new Date()) {
            status = 'Overdue';
          } else {
            status = 'Pending';
          }

          return {
            id: invoice.id,
            docNumber: invoice.invoiceNumber,
            issuedDate: invoice.issuedDate,
            dueDate,
            totalAmount: invoice.totalAmount ?? 0,
            amountDue: invoice.amountDue ?? 0,
            balance,
            status,
            publicInvoiceUrl: invoice.publicInvoiceUrl ?? '',
            salesLineItems: (invoice.lineItems ?? []).map((lineItem) => ({
              description: lineItem.description ?? '',
              itemName: lineItem.title,
              quantity: lineItem.quantity,
              unitPrice: lineItem.unitPrice,
              amount: lineItem.totalAmount,
            })),
          };
        }));
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError('Failed to load invoices');
        setInvoices([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInvoices();

    return () => {
      isMounted = false;
    };
  }, [dfoCustomerNumber]);

  const filteredInvoices = useMemo(() => {
    switch (filterOption) {
      case 'unpaid':
        return invoices.filter((item) => item.status.toLowerCase() !== 'paid');
      case 'paid':
        return invoices.filter((item) => item.status.toLowerCase() === 'paid');
      default:
        return invoices;
    }
  }, [filterOption, invoices]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / rowsPerPage));
    const lastValidPage = totalPages - 1;

    if (page > lastValidPage) {
      setPage(lastValidPage);
    }
  }, [filteredInvoices.length, page, rowsPerPage]);

  const paginatedInvoices = useMemo(
    () => filteredInvoices.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage),
    [filteredInvoices, page, rowsPerPage],
  );

  const handleFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFilterOption(event.target.value as FilterOption);
    setPage(0);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (rowsPerPageAmount: number) => {
    setPage(0);
    setRowsPerPage(rowsPerPageAmount);
  };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

  const currentBalance = invoices
    .filter((inv) => inv.status.toLowerCase() !== 'paid')
    .reduce((sum, inv) => sum + inv.balance, 0);

  if (loading) {
    return (
      <Grid container justify="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container justify="center">
        <Typography color="error">{error}</Typography>
      </Grid>
    );
  }

  return (
    <>
      <Divider />
      <div className={classes.headerText}>
        <span>Billing Information</span>
      </div>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.balancePaper} elevation={3}>
            <Typography variant="h6">Current Balance</Typography>
            <Typography variant="h3" className={classes.balanceAmount}>
              {formatCurrency(currentBalance)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Typography variant="h6" className={classes.invoicesHeader}>
                Invoice History
              </Typography>
            </Grid>
            <Grid item xs>
              <FormControl variant="outlined" size="small" style={{ minWidth: 150, marginLeft: 'auto' }}>
                <InputLabel id="dfo-invoice-filter-label">Filter</InputLabel>
                <Select
                  labelId="dfo-invoice-filter-label"
                  id="dfo-invoice-filter"
                  value={filterOption}
                  onChange={handleFilterChange}
                  label="Filter"
                >
                  <MenuItem value="all">All Invoices</MenuItem>
                  <MenuItem value="unpaid">Unpaid Invoices</MenuItem>
                  <MenuItem value="paid">Paid Invoices</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Paper className={classes.tablePaper}>
            <Table className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell />
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Amount Due</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.length > 0 ? (
                  paginatedInvoices.map((item) => (
                    <BillingDFORow
                      key={item.id}
                      invoice={item}
                      formatCurrency={formatCurrency}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No invoices found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {filteredInvoices.length > 0 && (
              <TablePagination
                allItemsLoaded
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                rowsPerPage={rowsPerPage}
                currentPage={page}
                rowsCount={paginatedInvoices.length}
                rowsTotal={filteredInvoices.length}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default React.memo(BillingDFO);
